/**
 * accountPartition (#2005) — split the tenant's ``cash_flow`` entities into one
 * engine-record set per bank account, so each account's balance series runs on
 * ITS OWN flows against ITS OWN anchor (fixing the pre-#2001 mixed-saldo defect).
 *
 * Routing for USER plans (#2211):
 *  - a plan carrying an explicit ``account_id`` that matches a real account routes
 *    onto THAT account's series (the multi-account write path stamps it; a reassign
 *    is just an edit that re-stamps it → the plan re-homes between accounts);
 *  - an account-less plan (single-account / legacy write path with no account
 *    dimension), or one whose ``account_id`` matches no real account, falls back to
 *    D2: the legacy ``fa-liquiditaet`` alias when present (so a migrated single-bank
 *    tenant like Nina is preserved exactly), else the PRIMARY account — the one
 *    flagged ``isOverlayPrimary`` (input-order-first, fixed before the #2089 display
 *    sort so re-sorting for the view never re-routes a plan).
 *
 * Either way the plan reaches "Alle Konten" (the aggregate = flatten of the
 * per-account sets) exactly once — D2's "overlay onto Alle Konten always".
 *
 * This module deliberately reuses ``cashFlowsToRecords`` from ``cashFlowProjection``
 * (the frozen parity module) and mirrors its user-plan overlay locally rather than
 * editing it — ``engineRecordsFrom``'s empty-cash_flows fallback returns ALL fact
 * records, which is wrong per-account, so the overlay is reproduced here scoped to
 * the account-less user plans only.
 */
import { cashFlowsToRecords } from './cashFlowProjection';
import type { LiquidityAccount } from './accounts';
import type { LiquiditaetRecord, StatementBalance } from './types';

/**
 * The statements that belong to ONE account's balance run (#2690).
 *
 * ``liquiditaet_statement_balance`` carries its account on the fact ANCHOR, which
 * ``toStatement`` lifts to an optional ``account_id`` (the #2004 lock keeps it out
 * of ``fact_value``; the anchor IS the dimension). Routing:
 *
 *  - a statement naming THIS account → this run;
 *  - an account-less statement (the seed-era shape) → this run only when it is the
 *    **adopting** run: the ``fa-liquiditaet`` alias in the multi-account fold, or
 *    the single-account run. Byte-identical to the pre-#2690
 *    ``account.isAlias ? statements : []``;
 *  - a statement naming a DIFFERENT account, or one no surviving account matches
 *    (e.g. anchored on a #2636-filtered revenue bucket) → **dropped**.
 *
 * That last rule is deliberately unlike the user-plan routing below, which re-homes
 * an unroutable plan onto the overlay account. A plan is money — dropping it
 * under-reports the balance, so a fallback home is the safer error. A statement is
 * an ATTESTATION about one Konto: it advances that account's Ist horizon and
 * extends its drawn history. Re-homing one would mark a span of another account's
 * curve "confirmed actual" on evidence that says nothing about it.
 *
 * Mirror of the backend ``liquiditaet_projection.statements_for_account``.
 */
export function statementsForAccount(
  statements: StatementBalance[],
  accountId: string | undefined,
  adoptUnanchored: boolean,
): StatementBalance[] {
  // HOF-97 (codex round 2, P1) — an adopted row is routed but MARKED. Adoption is a
  // generosity about the Ist horizon, not evidence about a balance: this row named no
  // account, so it may attest to a different Konto or be a legacy aggregate.
  // ``statementAnchors`` refuses to re-seed on a marked row; nothing else changes.
  // Mirror of ``liquiditaet_projection.statements_for_account``.
  return statements.flatMap((s) => {
    if (s.account_id) return s.account_id === accountId ? [s] : [];
    return adoptUnanchored ? [{ ...s, anchor_adopted: true }] : [];
  });
}

export interface EngineRecordPartition {
  /** account_id → the engine records feeding THAT account's balance series. */
  byAccount: Map<string, LiquiditaetRecord[]>;
  /** The union across accounts (= the whole-tenant feed; no orphan cash_flows). */
  all: LiquiditaetRecord[];
  /**
   * HOF-135 §1.4 — the ``account_id``s carried by ``cash_flow`` rows that match NO
   * surviving account, and how many rows that is.
   *
   * The partition has always dropped them (it iterates accounts and filters flows to
   * each), and dropping is right: a flow whose Konto is gone must not land on some
   * other account's curve. What was missing is that the drop was **silent**.
   *
   * Tombstoning a ``financial_account`` does not cascade to its ``cash_flow`` rows, so
   * every ghost sweep leaves its flows behind — WF carried 92 such rows across five
   * tombstoned counterparty-IBAN accounts on 2026-08-27, found only by hand-querying
   * the twin. They cost the balance nothing, but they are live rows that re-attach the
   * moment their deterministic ``uuid5`` account re-mints, and they still render in the
   * Ledger. Counting them here turns "invisible until someone goes looking" into a
   * number the data-quality surface can state.
   *
   * Deliberately a COUNT plus the ids, not the rows: the consumer is a disclosure, not
   * a recovery path. Re-homing an orphan is an identity decision (HOF-97 §6 L1) and
   * belongs to a migration, never to a render.
   */
  orphanedAccountIds: string[];
  orphanedFlowCount: number;
}

/** The account-less user plans (live write-of-record) that overlay per D2. */
function userPlansFrom(factRecords: LiquiditaetRecord[]): LiquiditaetRecord[] {
  return factRecords
    .filter((r) => r.record_role === 'planned' && r.source === 'user')
    // Re-key to the ``derived_fact_id`` so an overlaid plan groups with the
    // entity payed sibling that reconstructs the same id from ``planned_ref``
    // (keeps the #1653 "payed supersedes its own plan" drop working —
    // scheme-tolerant since #3171: the drop also matches the strip-normalised
    // id, so a settling credit keyed on the bare entity scheme supersedes too).
    .map((r) => (r.derived_fact_id ? { ...r, transaction_id: r.derived_fact_id } : r));
}

/** ``cash_flow`` records for one account + (for the overlay account) the user
 *  plans, minus any already-projected copy of those plans (no double-count). */
function accountRecords(
  accountFlows: Record<string, unknown>[],
  userPlanned: LiquiditaetRecord[],
): LiquiditaetRecord[] {
  const userPlannedIds = new Set(userPlanned.map((r) => r.transaction_id));
  // Empty-id guard, same reasoning as ``engineRecordsFrom`` (#2611): aggregated
  // buckets carry no ``source_ref``, so an empty-vs-empty match would drop the
  // whole per-account history instead of a handful of rows.
  const entityRecords = cashFlowsToRecords(accountFlows).filter(
    (r) => !r.derived_fact_id || !userPlannedIds.has(r.derived_fact_id),
  );
  return [...entityRecords, ...userPlanned];
}

export function partitionEngineRecords(
  cashFlows: Record<string, unknown>[],
  factRecords: LiquiditaetRecord[],
  accounts: LiquidityAccount[],
  /**
   * HOF-135 §1.4 — ids whose flows are excluded BY DESIGN (#2636/#2664 revenue
   * buckets, the ``__tenant_total__`` roll-up sentinel), as produced by
   * ``revenueBucketAccountIds``.
   *
   * Load-bearing for the orphan count ONLY — the partition itself already ignores
   * them, because they are not in ``accounts``. Without it the two reasons a flow is
   * absent from the curve collapse into one number: WF's ``fa-liquiditaet`` bucket
   * alone holds 7.462 deliberately-excluded Hofladen rows, which would have swamped
   * the 92 genuinely-orphaned ones and reported a healthy tenant as broken. Defaulted
   * so existing callers and fixtures keep compiling; a caller that omits it gets the
   * pre-HOF-135 partition plus a count that is merely conservative-high.
   */
  excludedAccountIds: ReadonlySet<string> = new Set(),
): EngineRecordPartition {
  const userPlanned = userPlansFrom(factRecords);
  const accountIds = new Set(accounts.map((a) => a.id));
  // Fallback overlay target: the account flagged ``isOverlayPrimary`` (alias if
  // present, else the input-order-first account — fixed BEFORE the #2089 display
  // sort so the A–Z view order never re-routes a plan). Fall back to accounts[0]
  // only if the flag is somehow absent (empty list / hand-built fixtures).
  const overlayId =
    accounts.find((a) => a.isOverlayPrimary)?.id ?? accounts[0]?.id ?? null;
  // Each user plan's owning account: its explicit ``account_id`` when that matches a
  // real account (#2211 write path / reassign), else the D2 overlay-primary. Every
  // plan therefore has exactly ONE owner among the real accounts → Σ ≡ Alle Konten.
  const planOwnerOf = (r: LiquiditaetRecord): string | null =>
    r.account_id && accountIds.has(r.account_id) ? r.account_id : overlayId;

  const byAccount = new Map<string, LiquiditaetRecord[]>();
  const all: LiquiditaetRecord[] = [];
  for (const account of accounts) {
    const accountFlows = cashFlows.filter((cf) => cf.account_id === account.id);
    const overlay = userPlanned.filter((r) => planOwnerOf(r) === account.id);
    // Tag each record with its owning account (display dimension) — the engine
    // math itself keys off the partition, not this field.
    const records = accountRecords(accountFlows, overlay).map((r) => ({
      ...r,
      account_id: account.id,
    }));
    byAccount.set(account.id, records);
    all.push(...records);
  }

  const { orphanedAccountIds, orphanedFlowCount } = orphanedFlowStats(
    cashFlows,
    accounts,
    excludedAccountIds,
  );
  return { byAccount, all, orphanedAccountIds, orphanedFlowCount };
}

/**
 * HOF-135 §1.4 — the ``cash_flow`` rows whose ``account_id`` matches no surviving
 * account and is not excluded by design.
 *
 * Exported and standalone because the SINGLE-account path never partitions:
 * ``useLiquiditaetsplanung`` short-circuits ``accountViews`` at ``accounts.length <= 1``,
 * so a count computed only inside ``partitionEngineRecords`` would report zero orphans
 * for exactly the tenants whose one Konto was tombstoned — the shape most likely to
 * have them. One implementation, two callers, so the two can never disagree.
 *
 * Counted over the RAW flows, not by differencing the partition: an orphan never enters
 * ``byAccount``, so there is nothing downstream left to subtract from. A row with no
 * readable ``account_id`` is NOT an orphan — it never claimed a Konto, and reporting a
 * parse gap as a tombstone gap would send an operator hunting for a deletion that never
 * happened.
 */
export function orphanedFlowStats(
  cashFlows: Record<string, unknown>[],
  accounts: LiquidityAccount[],
  excludedAccountIds: ReadonlySet<string> = new Set(),
): { orphanedAccountIds: string[]; orphanedFlowCount: number } {
  const accountIds = new Set(accounts.map((a) => a.id));
  const counts = new Map<string, number>();
  for (const cf of cashFlows) {
    const id = cf.account_id;
    if (typeof id !== 'string' || id.length === 0) continue;
    if (accountIds.has(id) || excludedAccountIds.has(id)) continue;
    // BOOKINGS, not buckets. Since #2611 the feed is normally
    // ``aggregateGroupsToCashFlows`` output — one synthetic row per
    // ``account_id × value_date × status × planned_ref`` carrying its true
    // ``source_count``. Counting rows would report "however many distinct days those
    // orphans fall on", which is not the number the disclosure says it is and is not
    // what an operator would go looking for. ``source_count`` is absent on the raw
    // page-walk fallback, where one row IS one booking — hence the 1.
    const rows = cf.source_count;
    counts.set(id, (counts.get(id) ?? 0) + (typeof rows === 'number' && rows > 0 ? rows : 1));
  }
  return {
    orphanedAccountIds: [...counts.keys()].sort(),
    orphanedFlowCount: [...counts.values()].reduce((n, c) => n + c, 0),
  };
}
